import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { safeReturnTo } from '../utils/safeReturnTo';

const API_BASE_URL = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? '' : 'http://localhost:5000');
const REDIRECT_DELAY_MS = 1500;

const AuthCallback: React.FC = () => {
  const [status, setStatus] = useState<'processing' | 'success' | 'error'>('processing');
  const [errorMessage, setErrorMessage] = useState<string>('');
  // StrictMode runs effects twice in dev; the refresh token rotates on use,
  // so a second POST /refresh would invalidate the first.
  const startedRef = useRef(false);

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    // Drop any stale token synchronously so AuthProvider's init skips /me
    // and can't race the refresh below.
    localStorage.removeItem('accessToken');
    handleAuthCallback();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAuthCallback = async () => {
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const returnTo = safeReturnTo(urlParams.get('returnTo') || '/');
      const error = urlParams.get('error');

      if (error) {
        setErrorMessage(error);
        setStatus('error');
        return;
      }

      // The OAuth callback set the httpOnly refresh cookie; exchange it for
      // an access token instead of receiving one in the URL.
      const response = await axios.post(`${API_BASE_URL}/api/auth/refresh`, {}, {
        withCredentials: true,
      });
      const accessToken = response.data?.accessToken;
      if (!accessToken) {
        setErrorMessage('No authentication token received');
        setStatus('error');
        return;
      }

      localStorage.setItem('accessToken', accessToken);
      setStatus('success');

      // Redirect after a short delay to show success message
      setTimeout(() => {
        window.location.assign(returnTo);
      }, REDIRECT_DELAY_MS);

    } catch (error) {
      console.error('Auth callback error:', error);
      setErrorMessage('Authentication processing failed');
      setStatus('error');
    }
  };

  const handleRetryLogin = () => {
    window.location.href = '/login';
  };

  if (status === 'processing') {
    return (
      <div className="min-h-screen bg-canvas flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <div className="flex justify-center">
            <img
              src="/eskimi-logo-medium.png"
              alt="Eskimi Logo"
              className="h-12 w-auto"
            />
          </div>
          <h2 className="mt-6 text-center text-2xl font-bold text-ink">
            Completing Sign In
          </h2>
        </div>

        <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
          <div className="bg-surface py-8 px-4 shadow sm:rounded-lg sm:px-10">
            <div className="flex flex-col items-center space-y-4">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent"></div>
              <p className="text-sm text-center text-ink-muted">
                Processing your authentication...
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div className="min-h-screen bg-canvas flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <div className="flex justify-center">
            <img
              src="/eskimi-logo-medium.png"
              alt="Eskimi Logo"
              className="h-12 w-auto"
            />
          </div>
          <h2 className="mt-6 text-center text-2xl font-bold text-ink">
            Sign In Successful
          </h2>
        </div>

        <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
          <div className="bg-surface py-8 px-4 shadow sm:rounded-lg sm:px-10">
            <div className="flex flex-col items-center space-y-4">
              <div className="w-12 h-12 rounded-full flex items-center justify-center bg-safe-bg">
                <svg className="w-6 h-6 text-safe" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <p className="text-sm text-center text-ink">
                Welcome to Eskimi Brand Safety!
              </p>
              <p className="text-xs text-center text-ink-muted">
                Redirecting you to the application...
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  return (
    <div className="min-h-screen bg-canvas flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex justify-center">
          <img
            src="/eskimi-logo-medium.png"
            alt="Eskimi Logo"
            className="h-12 w-auto"
          />
        </div>
        <h2 className="mt-6 text-center text-2xl font-bold text-ink">
          Sign In Failed
        </h2>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-surface py-8 px-4 shadow sm:rounded-lg sm:px-10">
          <div className="flex flex-col items-center space-y-4">
            <div className="w-12 h-12 rounded-full flex items-center justify-center bg-risk-bg">
              <svg className="w-6 h-6 text-risk" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <p className="text-sm text-center text-ink">
              Authentication Failed
            </p>
            <p className="text-xs text-center text-ink-muted">
              {errorMessage || 'An error occurred during sign in.'}
            </p>
            <button
              onClick={handleRetryLogin}
              className="mt-4 w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-accent hover:bg-accent-active focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthCallback;